import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/PageHeader";
import { API_BASE } from "@/lib/api";
import api from "@/lib/api";
import { Sparkles, Send, Loader2, Bot, User, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

const SUGGESTIONS = [
  "Quel est mon solde de trésorerie total ?",
  "Quels clients ont des factures impayées ?",
  "Quels produits sont sous le stock minimum ?",
  "Résume mes ventes et dépenses du mois",
];

function newSessionId() {
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function Assistant() {
  const { societeId, societeParam } = useApp();
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("gf_ai_session") || newSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("gf_ai_session", sessionId);
    api.get("/assistant/history", { params: { session_id: sessionId } })
      .then((r) => setMessages(r.data.map((m) => ({ role: m.role, content: m.content }))))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("gf_token");
      const res = await fetch(`${API_BASE}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          session_id: sessionId,
          message: msg,
          societe_id: societeParam.societe_id || null,
        }),
      });
      if (!res.ok || !res.body) throw new Error("stream error");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Désolé, une erreur est survenue. Réessayez." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const resetChat = () => {
    const sid = newSessionId();
    setSessionId(sid);
    setMessages([]);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]" data-testid="assistant-page">
      <div className="flex items-start justify-between">
        <PageHeader
          title="Assistant IA"
          subtitle="Posez vos questions sur vos données — propulsé par Claude"
          testid="assistant-header"
        />
        <button
          onClick={resetChat}
          data-testid="assistant-reset-btn"
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <Trash2 className="h-4 w-4" /> Nouvelle conversation
        </button>
      </div>

      <div className="flex-1 min-h-0 rounded-xl border bg-card flex flex-col overflow-hidden">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="assistant-messages">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                <Sparkles className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-lg">Comment puis-je vous aider ?</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                Je connais vos données ERP (trésorerie, ventes, dépenses, stock, tiers) pour la société sélectionnée.
              </p>
              <div className="grid sm:grid-cols-2 gap-2 mt-6 w-full max-w-xl">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    data-testid={`assistant-suggestion-${i}`}
                    className="text-left text-sm rounded-lg border px-3 py-2.5 hover:bg-accent hover:border-primary/40 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={cn("flex gap-3", m.role === "user" ? "justify-end" : "justify-start")}>
              {m.role === "assistant" && (
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
              )}
              <div
                data-testid={`assistant-msg-${m.role}`}
                className={cn(
                  "max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
                  m.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-sm whitespace-pre-wrap"
                    : "bg-muted text-foreground rounded-bl-sm"
                )}
              >
                {m.content ? (
                  m.role === "assistant" ? (
                    <div className="text-sm leading-relaxed [&_p]:my-1.5 [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_h1]:font-heading [&_h1]:font-bold [&_h1]:text-base [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-[15px] [&_h3]:font-heading [&_h3]:font-semibold [&_strong]:font-semibold [&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-background [&_th]:text-left [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_code]:bg-background [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[13px] [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    </div>
                  ) : (
                    m.content
                  )
                ) : (streaming && i === messages.length - 1 ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : "")}
              </div>
              {m.role === "user" && (
                <div className="h-8 w-8 rounded-lg bg-foreground/10 flex items-center justify-center shrink-0">
                  <User className="h-4 w-4" />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="border-t p-3">
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-end gap-2"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Écrivez votre question…"
              rows={1}
              data-testid="assistant-input"
              className="flex-1 resize-none rounded-xl border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary/50 max-h-32"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              data-testid="assistant-send-btn"
              className="h-11 w-11 shrink-0 rounded-xl bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 hover:opacity-90 transition-opacity"
            >
              {streaming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
